// src/core/chat.js — capa de datos del chat del grupo.
//
// FASE 4 · F1.3.d1. `mensajes_grupo` vive en Supabase Postgres. Chat de la
// pestaña Arengas: solo texto plano, sin multimedia, sin moderación.
//
// Lectura/escritura: SELECT/INSERT directo — la RLS lo permite a los miembros
// del grupo. La limpieza (mensajes > 7 días y tope de 50 por grupo) ya la hace
// el cron `job_mantenimiento` server-side; acá la lectura además limita a los
// últimos 50 visibles (spec Chat v1.0).
//
// Bug #1 (skeleton + caché): los mensajes se cachean en memoria con TTL corto
// — un cambio de tab en el embed de rituales no re-fetchea el chat. Se invalida
// al enviar un mensaje; con Realtime (F1.4.b) la invalidación será por push.

import { supabase } from '../supabase.js'

const MAX_VISIBLES = 50
const MENSAJES_TTL_MS = 60_000
const _mensajesCache = new Map()    // groupId → { value, at }
const _mensajesInflight = new Map() // groupId → Promise (request en vuelo)

// Invalida la caché de mensajes: un grupo puntual, o toda (sin argumento).
export const invalidarCacheMensajes = (groupId) => {
  if (groupId) {
    _mensajesCache.delete(groupId)
    _mensajesInflight.delete(groupId)
  } else {
    _mensajesCache.clear()
    _mensajesInflight.clear()
  }
}

// Últimos 50 mensajes del grupo, no vencidos (< 7 días), en orden cronológico
// (más viejo primero). Incluye el apodo del autor y si es jugador extranjero.
// Cacheado (TTL corto) + dedup de requests en vuelo.
export const getMensajesDelGrupo = async (groupId) => {
  if (!groupId) return []
  const hit = _mensajesCache.get(groupId)
  if (hit && Date.now() - hit.at < MENSAJES_TTL_MS) return hit.value
  if (_mensajesInflight.has(groupId)) return _mensajesInflight.get(groupId)
  const promise = (async () => {
    try {
      const desde = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      const { data, error } = await supabase
        .from('mensajes_grupo')
        .select('*, jugadores(display_name, usuarios(tipo))')
        .eq('group_id', groupId)
        .gte('created_at', desde)
        .order('created_at', { ascending: false })
        .limit(MAX_VISIBLES)
      if (error) console.error('[chat] getMensajesDelGrupo:', error.message)
      // se pidió DESC + LIMIT 50 (los 50 más recientes); se devuelve ASC para pintar.
      const value = error ? [] : (data || []).slice().reverse()
      _mensajesCache.set(groupId, { value, at: Date.now() })
      return value
    } finally {
      _mensajesInflight.delete(groupId)
    }
  })()
  _mensajesInflight.set(groupId, promise)
  return promise
}

// Envía un mensaje al chat del grupo. INSERT directo (RLS: miembro del grupo +
// es tu propio player_id). `contenido` es texto plano (máx 500 chars en la DB).
// Invalida la caché del grupo para que el próximo render traiga el mensaje.
// Devuelve { data } | { error }.
export const enviarMensaje = async (groupId, playerId, contenido) => {
  const texto = (contenido || '').trim()
  if (!texto) return { error: { message: 'mensaje_vacio' } }
  const { data, error } = await supabase
    .from('mensajes_grupo')
    .insert({ group_id: groupId, player_id: playerId, contenido: texto })
    .select('*, jugadores(display_name, usuarios(tipo))')
  if (!error) invalidarCacheMensajes(groupId)
  return error ? { error } : { data: data?.[0] }
}
